package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*

/**
 * Court Light tone enum for [Pill] — maps to tinted bg + coloured text from
 * the Court Light palette.
 */
enum class PillTone {
    Neutral,
    Accent,
    Success,
    Warning,
    Danger,
    Pro
}

/**
 * Maps a sport type string to the appropriate [PillTone].
 * Shared utility used by Clubs and Events pages.
 */
fun sportPillTone(type: String): PillTone = when (type.uppercase().trim()) {
    "FOOTBALL", "SOCCER" -> PillTone.Accent
    "TENNIS" -> PillTone.Success
    "BASKETBALL" -> PillTone.Danger
    "VOLLEYBALL" -> PillTone.Warning
    else -> PillTone.Neutral
}

/**
 * Maps an event status string to the appropriate [PillTone].
 * upcoming → Accent, active/live → Success, finished/completed → Neutral, canceled → Danger.
 */
fun eventStatusPillTone(status: String): PillTone = when (status.uppercase().trim()) {
    "UPCOMING", "SCHEDULED" -> PillTone.Accent
    "ACTIVE", "LIVE", "ONGOING" -> PillTone.Success
    "FINISHED", "COMPLETED", "DONE" -> PillTone.Neutral
    "CANCELLED", "CANCELED" -> PillTone.Danger
    else -> PillTone.Neutral
}

/**
 * Tinted pill badge — small rounded label with a soft background tint.
 *
 * - Neutral  → ink faint bg, muted text
 * - Accent   → blue tint bg, accent text
 * - Success  → green tint bg, success text
 * - Warning  → amber tint bg, warning text
 * - Danger   → red tint bg, error text
 * - Pro      → gold gradient tint, ProGold text
 */
@Composable
fun Pill(
    text: String,
    tone: PillTone,
    modifier: Modifier = Modifier
) {
    val (bg, fg) = when (tone) {
        PillTone.Neutral -> Pair("rgba(255,255,255,0.06)", "#A7ADB9")
        PillTone.Accent  -> Pair("#0A1E42", "#8FB8FF")
        PillTone.Success -> Pair("#072A21", "#00E5A4")
        PillTone.Warning -> Pair("#262311", "#E4CD40")
        PillTone.Danger  -> Pair("#2A1714", "#FF6B5B")
        PillTone.Pro     -> Pair("linear-gradient(135deg, #262311 0%, #262311 100%)", "#E4CD40")
    }
    Box(
        modifier
            .padding(leftRight = 10.px, topBottom = 3.px)
            .sBorderRadius(999)
            .styleModifier {
                property("background", bg)
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        SpanText(
            text,
            Modifier.bodySMedium()
                .styleModifier {
                    property("color", fg)
                    property("font-weight", "600")
                    property("white-space", "nowrap")
                }
        )
    }
}
